/**
 * Helpers for resolving the SBA (FDA Review) application-type prefix
 * shown in the version selector and document viewer header.
 *
 * Background: parsers/sba_parser_v4.py extracts application_type from
 * each PDF cover page independently. When the cover-page layout defeats
 * the regex (common in older FDA Multi-Discipline Review templates
 * where the value sits in a separate text run from the label), the
 * field lands NULL in the database. Sibling reviews from the same
 * submission may have it set correctly. The frontend previously fell
 * back to a hard-coded "NDA" string, which fabricated incorrect labels
 * for BLA submissions (e.g., Imfinzi BLA 761069 displayed as
 * "FDA Reviews — NDA 761069" alongside "FDA Reviews — BLA 761069" in
 * the same drug).
 */

type ApplicationTypedDoc = {
  application_type?: string | null;
  application_number?: string | null;
};

/**
 * Allowlist of recognized FDA application-type codes. The pre-fix
 * parser had an `else: raw_app_type[:10]` fallback that could write
 * truncated body-text artifacts (e.g. "OF TREATME") into the field, so
 * any value outside this set is treated as missing rather than
 * displayed verbatim. The Alembic backfill nulls these out, but the
 * frontend guard catches stale data before a redeploy or during the
 * window between code deploy and migration run.
 */
const VALID_APPLICATION_TYPES: ReadonlySet<string> = new Set([
  'NDA',
  'BLA',
  'ANDA',
]);

function isValidApplicationType(value: string | null | undefined): value is string {
  return !!value && VALID_APPLICATION_TYPES.has(value.toUpperCase());
}

/**
 * Infer application type from the FDA application number when no
 * authoritative value is available. FDA assigns BLA numbers in the
 * 7xxxxx range (unambiguous). The 1xxxxx range overlaps legacy BLAs
 * with NDAs, so we don't guess there. Returns "" when ambiguous.
 */
export function deriveSbaApplicationType(
  appNumber: string | null | undefined,
): string {
  if (!appNumber) return '';
  if (/^7\d{5}$/.test(appNumber)) return 'BLA';
  return '';
}

/**
 * Resolve the application_type for an application number, preferring
 * (in order):
 *   1. ``ownType`` if provided and in the FDA allowlist — used when
 *      the caller is rendering a specific document's header rather
 *      than a sibling group, so the doc's own value wins over sibling
 *      propagation. Non-allowlist values fall through.
 *   2. Any sibling document with a matching ``application_number`` and
 *      an allowlist ``application_type`` — covers the partial-extraction
 *      case where one review in a submission parsed correctly and the
 *      others landed NULL or stale-garbage.
 *   3. Number-range inference (``deriveSbaApplicationType``).
 *   4. Empty string — caller should omit the prefix rather than guess.
 */
export function resolveSbaApplicationType(
  appNumber: string | null | undefined,
  siblings: ApplicationTypedDoc[],
  ownType?: string | null,
): string {
  if (isValidApplicationType(ownType)) return ownType.toUpperCase();
  if (!appNumber) return '';
  for (const sibling of siblings) {
    if (
      sibling.application_number === appNumber &&
      isValidApplicationType(sibling.application_type)
    ) {
      return sibling.application_type.toUpperCase();
    }
  }
  return deriveSbaApplicationType(appNumber);
}
